/**
 * chatSessionParams — the SINGLE registry of URL query params that bind a chat
 * surface to its active session.
 *
 * Each chat embedding persists its active session id in the URL so the chat
 * deep-links, survives a reload, and can be shared. Because more than one chat
 * surface can be mounted on the same page (the global Luma overlay floats above
 * any page that may ALSO host its own embedded chat), the params MUST be
 * mutually disjoint — a shared param meant a left-bound session on one surface
 * leaked into another and rendered two conversations side by side (the
 * `?luma=` vs `?chat=` leak that motivated this registry).
 *
 * This is the one place a surface's param is declared. A new embedding picks an
 * existing entry (page-embedded chats share `?chat=`) or adds a new disjoint
 * one — never a literal string at the call site. `chatSessionParams.test.ts`
 * asserts the values stay disjoint.
 */
export const CHAT_SESSION_PARAMS = {
  /**
   * Global Luma assistant (app-bar Sparkles) — present on every page, so it
   * owns a dedicated param that no page-embedded chat reads.
   */
  globalLuma: 'luma',
  /** AI Chat main page (`/chat`, `ChatPage`). */
  chatPage: 'session',
  /**
   * Page-embedded chats — entity detail panels (deal/task/website), the AI
   * agent overview panel, and the Forms & BPM wizard. Each page reads/writes
   * `?chat=` for its own embedded panel; never collides with the overlay above.
   */
  pageEmbedded: 'chat',
} as const;

/** A registered chat surface key. */
export type ChatSurface = keyof typeof CHAT_SESSION_PARAMS;

/** All registered param names — used by the disjointness guard test. */
export const CHAT_SESSION_PARAM_VALUES = Object.values(CHAT_SESSION_PARAMS);
